// ============================================================
// ENGINE V2 — SUGGESTION ENGINE
// ============================================================
// Runs after the mutation engine has updated trait scores.
// Two responsibilities:
//
//   1. Resolve the suggestion scope — which career path nodes
//      the student is eligible to receive suggestions for.
//
//   2. Build and rank suggestion objects for each eligible node
//      by computing the distance between the student's current
//      trait scores and each node's minimum thresholds.
//
// Scope rules:
//   'wide'     — student completed psychometric test.
//                Suggestions span the entire career tree.
//   'declared' — student manually declared a path (no test).
//                Suggestions come from subtrees of active nodes.
//                A student at the top (broad) level gets full
//                subtree suggestions for that broad category.
//
// Suggestion classification:
//   'positive'  — >= POSITIVE_MATCH_RATIO of traits meet their minimum
//   'near-miss' — overall distance >= NEAR_MISS_THRESHOLD but below positive
//   (excluded)  — overall distance < NEAR_MISS_THRESHOLD
//
// Within each class, suggestions are ranked by matchScore descending.
// ============================================================

import type {
  StudentProfile,
  CareerPathNode,
  ActiveNode,
  GeneralTrait,
  TraitDistance,
  SuggestionObject,
  SuggestionResult,
  SuggestionScope,
} from './types';
import {
  NEAR_MISS_THRESHOLD,
  POSITIVE_MATCH_RATIO,
  MAX_SUGGESTIONS,
} from './constants';

// ─── TREE TRAVERSAL ──────────────────────────────────────────

// Collect every node in the full career tree (flat list)
function collectAllNodes(nodes: CareerPathNode[]): CareerPathNode[] {
  const result: CareerPathNode[] = [];
  function walk(ns: CareerPathNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

// Collect all descendant nodes of a given node (excludes the node itself)
function collectDescendants(
  rootNodeId: string,
  careerTree: CareerPathNode[]
): CareerPathNode[] {
  const descendants: CareerPathNode[] = [];

  function findAndDescend(nodes: CareerPathNode[]): boolean {
    for (const node of nodes) {
      if (node.nodeId === rootNodeId) {
        // Found the root — collect everything below it
        gatherChildren(node.children);
        return true;
      }
      if (node.children.length > 0 && findAndDescend(node.children)) {
        return true;
      }
    }
    return false;
  }

  function gatherChildren(nodes: CareerPathNode[]) {
    for (const node of nodes) {
      descendants.push(node);
      if (node.children.length > 0) gatherChildren(node.children);
    }
  }

  findAndDescend(careerTree);
  return descendants;
}

// ─── SCOPE RESOLUTION ────────────────────────────────────────
// Determine which career path nodes are eligible for suggestion
// based on the student's scope and active nodes.

function resolveNodesInScope(
  student: StudentProfile,
  careerTree: CareerPathNode[]
): CareerPathNode[] {
  // Nodes the student already has active or completed — exclude these
  const occupiedNodeIds = new Set(student.activeNodes.map(n => n.nodeId));

  if (student.suggestionScope === 'wide') {
    // Psychometric path: suggest across the entire tree
    return collectAllNodes(careerTree).filter(
      n => !occupiedNodeIds.has(n.nodeId)
    );
  }

  // Declared path: suggest from the subtrees of each active node
  // De-duplicate nodes that appear in multiple subtrees
  const seen = new Set<string>();
  const result: CareerPathNode[] = [];

  for (const activeNode of student.activeNodes) {
    if (activeNode.status !== 'active') continue;

    const subtree = collectDescendants(activeNode.nodeId, careerTree);

    for (const node of subtree) {
      if (!occupiedNodeIds.has(node.nodeId) && !seen.has(node.nodeId)) {
        seen.add(node.nodeId);
        result.push(node);
      }
    }
  }

  return result;
}

// ─── CURRENT TRAIT SCORES ────────────────────────────────────
// Produce a map of traitId → current score to use when computing
// distance against a career node's minimum thresholds.
//
// Scope rules:
//   'wide'     → use general traits (broad psychometric profile)
//   'declared' → use career path traits from active nodes
//                (reflects what the student has actually done)
//                If a trait appears in multiple active nodes,
//                use the highest score (student's best showing).

function buildCurrentTraitScoreMap(
  student: StudentProfile,
  scope: SuggestionScope
): Map<string, number> {
  const scores = new Map<string, number>();

  if (scope === 'wide') {
    for (const trait of student.generalTraits) {
      scores.set(trait.traitId, trait.score);
    }
    return scores;
  }

  // Declared scope — use active node traits, highest score wins
  for (const node of student.activeNodes) {
    if (node.status !== 'active') continue;
    for (const trait of node.traits) {
      const existing = scores.get(trait.traitId);
      if (existing === undefined || trait.score > existing) {
        scores.set(trait.traitId, trait.score);
      }
    }
  }

  return scores;
}

// ─── SUGGESTION OBJECT BUILDER ───────────────────────────────

function buildSuggestionObject(
  node: CareerPathNode,
  currentScores: Map<string, number>
): SuggestionObject | null {
  // Nodes with no traits cannot be meaningfully scored
  if (node.traits.length === 0) return null;

  // Compute distance for every trait the career node requires
  const traitDistances: TraitDistance[] = node.traits.map(nodeTrait => {
    // Student's current score for this trait (0 if never encountered)
    const current = currentScores.get(nodeTrait.traitId) ?? 0;
    const distance = current - nodeTrait.minimumThreshold;

    return {
      traitId:  nodeTrait.traitId,
      name:     nodeTrait.name,
      current,
      minimum:  nodeTrait.minimumThreshold,
      distance,
    };
  });

  const totalTraitCount    = traitDistances.length;
  const metThresholdCount  = traitDistances.filter(t => t.distance >= 0).length;
  const overallDistance    =
    traitDistances.reduce((sum, t) => sum + t.distance, 0) / totalTraitCount;

  // Exclude nodes that are too far below threshold to be useful suggestions
  if (overallDistance < NEAR_MISS_THRESHOLD) return null;

  // Classify the suggestion
  const metRatio = metThresholdCount / totalTraitCount;
  const type: SuggestionObject['type'] =
    metRatio >= POSITIVE_MATCH_RATIO ? 'positive' : 'near-miss';

  // Normalise match score to 0–1
  // Component 1 (60%): ratio of traits meeting threshold
  // Component 2 (40%): normalised overall distance (clamped to [0, 1])
  const normalisedDistance = Math.max(0, Math.min(1, (overallDistance + 100) / 200));
  const matchScore = metRatio * 0.6 + normalisedDistance * 0.4;

  return {
    node,
    traitDistances,
    overallDistance,
    metThresholdCount,
    totalTraitCount,
    matchScore,
    type,
  };
}

// ─── SUGGESTION ENGINE ────────────────────────────────────────

export function runSuggestionEngine(
  student: StudentProfile,
  careerTree: CareerPathNode[],
  updatedGeneralTraits: GeneralTrait[]
): SuggestionResult {
  // 1. Determine which nodes the student can receive suggestions for
  const nodesInScope = resolveNodesInScope(student, careerTree);

  // 2. Build the student's current trait score map
  const currentScores = buildCurrentTraitScoreMap(
    student,
    student.suggestionScope
  );

  // 3. Score every in-scope node
  const rawSuggestions: SuggestionObject[] = [];

  for (const node of nodesInScope) {
    const suggestion = buildSuggestionObject(node, currentScores);
    if (suggestion) rawSuggestions.push(suggestion);
  }

  // 4. Sort — positives first (best match score descending),
  //    then near-misses (closest to threshold descending)
  const positives = rawSuggestions
    .filter(s => s.type === 'positive')
    .sort((a, b) => b.matchScore - a.matchScore);

  const nearMisses = rawSuggestions
    .filter(s => s.type === 'near-miss')
    .sort((a, b) => b.overallDistance - a.overallDistance);

  const suggestions = [...positives, ...nearMisses].slice(0, MAX_SUGGESTIONS);

  return {
    studentId:           student.studentId,
    scope:               student.suggestionScope,
    suggestions,
    updatedGeneralTraits,
    timestamp:           new Date(),
  };
}
